import type {
  ApiCustomer,
  ApiService,
  ApiOrder,
  ApiPayment,
  JournalEntry,
  OrderHistoryEntry,
  ApiListResponse,
  ApiDetailResponse,
  CreateOrderResponse,
  CreatePaymentResponse,
  ProfitLossReport,
  BalanceSheetReport,
  SalesSummaryData,
  AuthResponse,
  MfaSetupResponse,
  MfaVerifyResponse,
  AccountNode,
  RawApiCustomer,
  RawApiService,
  RawInvoice,
  ApiExpense,
} from '@/types';
import type { CustomerFormData, PaymentFormData, LoginFormData, ExpenseFormData } from '@/types/schemas';
import { useSettingsStore } from '@/store/settings-store';

const rawApiUrl = import.meta.env.VITE_API_URL || '';
// Force relative URL in development to use Vite proxy (prevents hardcoded 3000 overrides)
export const API_URL = import.meta.env.DEV ? '/api' : (rawApiUrl.endsWith('/api') ? rawApiUrl : `${rawApiUrl}/api`);

if (import.meta.env.PROD) {
  console.info(`[API] Active Production URL: ${API_URL}`);
}


// ─── Error Class ─────────────────────────────────────────
export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public code?: string,
    public data?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ─── API Client ──────────────────────────────────────────
interface RequestOptions extends RequestInit {
  retries?: number;
  retryDelay?: number;
}

/**
 * APIClient
 * 
 * A versatile, singleton HTTP client for the frontend application.
 * Highlights:
 * 1. Automatically includes credentials (httpOnly cookies) for authorization.
 * 2. Implements automatic retries with exponential backoff for 5xx and 429 responses.
 * 3. Standardizes API error handling, redirecting to login on 401 Unauthorized.
 * 4. Provides a strong typing layer over generic fetch requests.
 */
class APIClient {
  /**
   * Core request engine.
   * Handles attach authorization headers, parsing JSON responses,
   * intercepting 401s for forced logout, and auto-retrying 5xx/429 errors.
   * 
   * @param endpoint - The API endpoint
   * @param options - Fetch options including retries and retryDelay
   * @param attempt - Current retry attempt counter
   * @returns Unwrapped JSON promise
   * @throws {ApiError} containing status code and server message
   */
  private isRefreshing = false;
  private failedQueue: Array<{ resolve: (token: string) => void; reject: (err: any) => void }> = [];

  private processQueue(error: Error | null, token: string | null = null) {
    this.failedQueue.forEach(prom => {
      if (error) {
        prom.reject(error);
      } else if (token) {
        prom.resolve(token);
      }
    });
    this.failedQueue = [];
  }

  private async request<T>(endpoint: string, options?: RequestOptions, attempt: number = 0): Promise<T> {
    const resilience = useSettingsStore.getState().resilience;
    const chaosHeaderValue = [];
    if (resilience?.latencyMs > 0) chaosHeaderValue.push(`latency=${resilience.latencyMs}`);
    if (resilience?.errorRate > 0) chaosHeaderValue.push(`error=${resilience.errorRate}`);

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(chaosHeaderValue.length > 0 ? { 'x-laundry-chaos-test': chaosHeaderValue.join(',') } : {}),
      ...(options?.headers as Record<string, string> ?? {}),
    };

    const fullUrl = `${API_URL}${endpoint}`;
    
    if (import.meta.env.DEV) {
      console.log(`[API] ${options?.method || 'GET'} ${fullUrl}`);
    }
    const maxRetries = options?.retries ?? 3;
    const baseDelay = options?.retryDelay ?? 1000;

    try {
      const response = await fetch(fullUrl, {
        ...options,
        headers,
        credentials: 'include',
        signal: options?.signal,
      });

      if (!response.ok) {
        // Don't retry 4xx errors (client errors) except 429 (rate limit)
        if (response.status !== 429 && response.status >= 400 && response.status < 500) {
          if (response.status === 401) {
            const isAuthRequest = endpoint.includes('/auth/login') || endpoint.includes('/auth/refresh');
            
            if (!isAuthRequest) {
                if (this.isRefreshing) {
                  console.log('[API] 401 detected. Queuing request while token refreshes...');
                  await new Promise<string>((resolve, reject) => {
                    this.failedQueue.push({ resolve, reject });
                  });
                  
                  console.log('[API] Token refreshed from queue. Retrying request...');
                  return this.request<T>(endpoint, options, attempt);
                } else {
                  this.isRefreshing = true;
                  console.log('[API] 401 detected. Initiating token refresh...');
                  try {
                    const refreshRes = await fetch(`${API_URL}/auth/refresh`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      credentials: 'include'
                    });
                    
                    if (refreshRes.ok) {
                      const data: AuthResponse = await refreshRes.json();
                      if (typeof localStorage !== 'undefined' && data.user) {
                        const user = localStorage.getItem('user');
                        if (user) {
                          try {
                              const parsed = JSON.parse(user);
                              localStorage.setItem('user', JSON.stringify({ ...parsed, ...data.user }));
                          } catch(e) {
                            console.error('[API] Failed to update cached user:', e);
                          }
                        }
                      }
                      console.log('[API] Token refresh successful. Processing queue...');
                      this.processQueue(null, "refreshed");
                      return this.request<T>(endpoint, options, attempt);
                    } else {
                       throw new Error('Refresh failed');
                    }
                  } catch (e) {
                    console.error('[API] Token refresh error:', e);
                    this.processQueue(e as Error, null);
                  } finally {
                    this.isRefreshing = false;
                  }
                }

              // Clear auth data and redirect to login if refresh fails or no refresh token
              console.log('[API] 401 detected and refresh failed/absent. Logging out...');
              if (typeof localStorage !== 'undefined') {
                localStorage.removeItem('user');
              }
              if (typeof window !== 'undefined') {
                window.location.href = '/login?expired=true';
              }
              throw new ApiError('Session expired - Please login again', 401, 'UNAUTHORIZED');
            }
          }

          let message = 'Request failed';
          const errorData = await response.json().catch(() => ({})); 
          
          if (import.meta.env.DEV) {
            console.error(`API Error ${response.status} at ${fullUrl}:`, JSON.stringify(errorData, null, 2));
          }
          
          if (typeof errorData === 'object' && errorData && 'error' in errorData) {
            const err = errorData as { error: { message?: string } };
            message = err.error?.message || message;
          }
          throw new ApiError(message, response.status, undefined, errorData);
        }

        // Retry 5xx errors and 429 rate limit
        if (attempt < maxRetries) {
          const delay = baseDelay * Math.pow(2, attempt); // Exponential backoff
          await new Promise(resolve => setTimeout(resolve, delay));
          return this.request<T>(endpoint, options, attempt + 1);
        }

        const message = 'Request failed';
        const errorData = await response.json().catch(() => ({}));
        throw new ApiError(message, response.status, undefined, errorData);
      }

      // Handle 204 No Content
      if (response.status === 204) return undefined as T;
      return response.json();
    } catch (e) {
      // Don't log AbortError as it's an intentional cancellation
      if (e instanceof Error && e.name === 'AbortError') {
        throw new ApiError('Request cancelled', 0, 'ABORT_ERROR');
      }

      // Retry network errors
      if (e instanceof TypeError && attempt < maxRetries) {
        const delay = baseDelay * Math.pow(2, attempt);
        await new Promise(resolve => setTimeout(resolve, delay));
        return this.request<T>(endpoint, options, attempt + 1);
      }
      
      if (import.meta.env.DEV) {
        console.error(`Request Failed to ${fullUrl}:`, e);
      }
      
      // Wrap network/connection errors in ApiError for consistent handling
      if (e instanceof TypeError) {
        throw new ApiError('Unable to connect to the server. Please check your internet connection and try again.', 0, 'NETWORK_ERROR');
      }
      
      throw e;
    }
  }

  // ─── Auth ──────────────────────────────────────────
  auth = {
    login: (data: LoginFormData) =>
      this.request<AuthResponse>('/auth/login', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    completeWalkthrough: () =>
      this.request<{ success: boolean }>('/auth/walkthrough-complete', {
        method: 'PATCH',
      }),
    mfaSetup: () =>
      this.request<MfaSetupResponse>('/auth/mfa/setup', {
        method: 'POST',
      }),
    mfaVerify: (token: string) =>
      this.request<MfaVerifyResponse>('/auth/mfa/verify', {
        method: 'POST',
        body: JSON.stringify({ token }),
      }),
    mfaVerifyLogin: (token: string) =>
      this.request<AuthResponse>('/auth/mfa/verify-login', {
        method: 'POST',
        body: JSON.stringify({ token }),
      }),
  };

  private qs(params?: Record<string, unknown>): string {
    if (!params) return '';
    const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '');
    return entries.length ? '?' + new URLSearchParams(entries.map(([k, v]) => [k, String(v)])).toString() : '';
  }

  // ─── Customers ───────────────────────────────────────
  customers = {
    list: async (params?: { search?: string; page?: number }) => {
      const res = await this.request<ApiListResponse<RawApiCustomer>>(`/customers${this.qs(params)}`);
      return {
        ...res,
        data: (res.data || []).map((c, i) => ({
          ...c,
          customerNumber: c.customerNumber || `CUST-${String(i + 1).padStart(4, '0')}`,
          updatedAt: c.updatedAt || c.createdAt,
        })),
      } as ApiListResponse<ApiCustomer>;
    },
    get: (id: string | number) =>
      this.request<ApiDetailResponse<ApiCustomer>>(`/customers/${id}`),
    create: (data: CustomerFormData) =>
      this.request<ApiDetailResponse<ApiCustomer>>('/customers', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    update: (id: string | number, data: Partial<CustomerFormData>) =>
      this.request<ApiDetailResponse<ApiCustomer>>(`/customers/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    delete: (id: string | number) =>
      this.request<void>(`/customers/${id}`, { method: 'DELETE' }),
    getOrders: async (id: string | number, params?: { page?: number; limit?: number }) => {
      // Try to fetch from customer-specific endpoint first, fallback to filtering orders
      try {
        const res = await this.request<ApiListResponse<any>>(`/customers/${id}/orders${this.qs(params)}`);
        return {
          ...res,
          data: (res.data || []).map((inv: RawInvoice) => this.mapInvoiceToOrder(inv)),
        } as ApiListResponse<ApiOrder>;
      } catch {
        // Fallback: fetch all orders and filter by customer
        const res = await this.request<ApiListResponse<any>>(`/invoices${this.qs({ ...params, customerId: id })}`);
        return {
          ...res,
          data: (res.data || []).map((inv: RawInvoice) => this.mapInvoiceToOrder(inv)),
        } as ApiListResponse<ApiOrder>;
      }
    },
  };

  // ─── Services ────────────────────────────────────────
  services = {
    list: async (params?: { unit?: string }) => {
      const res = await this.request<ApiListResponse<RawApiService>>(`/services${this.qs(params)}`);
      return {
        ...res,
        data: (res.data || []).map((s) => ({
          ...s,
          unitPrice: parseFloat(String(s.pricePerUnit)) || 0,
          code: s.code || '',
          durationHours: s.durationHours || 0,
        })),
      } as ApiListResponse<ApiService>;
    },
    get: async (id: string) => {
      const res = await this.request<RawApiService>(`/services/${id}`);
      return {
        data: {
          ...res,
          unitPrice: parseFloat(String(res.pricePerUnit)) || 0,
          code: res.code || '',
          durationHours: res.durationHours || 0,
        },
      } as ApiDetailResponse<ApiService>;
    },
    create: async (data: { name: string; pricePerUnit: number; unit: string; description?: string; minimumQty?: number; isActive?: boolean }) => {
      const res = await this.request<RawApiService>('/services', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      return {
        data: {
          ...res,
          unitPrice: parseFloat(String(res.pricePerUnit)) || 0,
          code: res.code || '',
          durationHours: res.durationHours || 0,
        },
      } as ApiDetailResponse<ApiService>;
    },
    update: async (id: string, data: { name?: string; pricePerUnit?: number; unit?: string; description?: string; minimumQty?: number; isActive?: boolean }) => {
      const res = await this.request<RawApiService>(`/services/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
      return {
        data: {
          ...res,
          unitPrice: parseFloat(String(res.pricePerUnit)) || 0,
          code: res.code || '',
          durationHours: res.durationHours || 0,
        },
      } as ApiDetailResponse<ApiService>;
    },
    delete: async (id: string) => {
      return this.request<{ id: string; deleted: boolean }>(`/services/${id}`, {
        method: 'DELETE',
      });
    },
    updateMaterials: async (serviceId: string, materials: Array<{ stockItemId: string; qtyPerUnit: number }>) => {
      return this.request<any>(`/services/${serviceId}/materials`, {
        method: 'PUT',
        body: JSON.stringify({ materials }),
      });
    },
  };

  // ─── Orders (Mapped to Invoices) ──────────────────────
  private mapInvoiceToOrder(invoice: RawInvoice): ApiOrder {
    const totalAmount = parseFloat(String(invoice.totalAmount)) || 0;
    const paidAmount = parseFloat(String(invoice.paidAmount)) || 0;
    
    // Map creator from backend response (createdBy, user, or creator field)
    const creator = invoice.createdBy || invoice.user || invoice.creator;
    
    return {
      id: invoice.id,
      orderNumber: invoice.invoiceNo,
      customerId: invoice.customerId,
      customer: invoice.customer ? {
        id: invoice.customer.id,
        name: invoice.customer.name,
        phone: invoice.customer.phone || null,
        address: invoice.customer.address || null,
      } : { id: '', name: 'Unknown', phone: null, address: null },
      orderDate: invoice.createdAt,
      totalAmount,
      paymentStatus: paidAmount >= totalAmount ? 'PAID' : (paidAmount > 0 ? 'PARTIAL' : 'UNPAID'),
      orderStatus: (invoice.status as ApiOrder['orderStatus']) || 'RECEIVED',
      notes: invoice.notes || null,
      items: (invoice.lines || []).map((line) => ({
        id: line.id,
        orderId: invoice.id,
        serviceId: line.serviceId,
        service: { 
          id: line.serviceId, 
          name: line.service ? line.service.name : (line.description || 'Unknown Service'), 
          unit: (line.unit || 'PCS') as ApiService['unit']
        },
        quantity: parseFloat(String(line.qty)) || 0,
        unitPrice: parseFloat(String(line.unitPrice)) || 0,
        subtotal: parseFloat(String(line.subtotal)) || 0,
        status: 'PENDING' as const,
      })),
      journalEntryId: invoice.journalEntryId || invoice.journalJobId || null,
      createdBy: creator ? {
        id: creator.id,
        name: creator.name || creator.fullName || 'Unknown',
        email: creator.email,
      } : null,
      createdAt: invoice.createdAt,
      updatedAt: invoice.createdAt,
    };
  }

  private normalizePaymentMethod(method: unknown): ApiPayment['paymentMethod'] {
    if (typeof method !== 'string') return 'CASH';
    const normalized = method.trim().toUpperCase().replace(/\s+/g, '_');

    if (['BANK_TRANSFER', 'BCA', 'MANDIRI', 'TRANSFER', 'BANK'].includes(normalized)) {
      return 'BANK_TRANSFER';
    }

    if (['GOPAY', 'GO_PAY', 'GO-PAY', 'OVO', 'DANA', 'SHOPEEPAY', 'E_WALLET', 'EWALLET', 'QRIS'].includes(normalized)) {
      return 'GOPAY';
    }

    return 'CASH';
  }

  private mapPayment = (payment: any): ApiPayment => {
    const order = payment.order || payment.invoice || {};
    const orderId = String(payment.orderId || payment.invoiceId || order.id || '');
    const amount = Number(payment.amount ?? 0);

    return {
      id: payment.id,
      paymentNumber: payment.paymentNumber || `PAY-${String(payment.id || '').slice(0, 8).toUpperCase()}`,
      orderId,
      order: {
        id: orderId,
        orderNumber: order.orderNumber || order.invoiceNo || '-',
        totalAmount: Number(order.totalAmount ?? 0),
      },
      paymentDate: payment.paymentDate || payment.paidAt || payment.createdAt || new Date().toISOString(),
      amount: Number.isFinite(amount) ? amount : 0,
      paymentMethod: this.normalizePaymentMethod(
        payment.paymentMethodName || payment.paymentMethod?.name || payment.paymentMethod,
      ),
      notes: payment.notes || null,
      journalEntryId: payment.journalEntryId || payment.journalJobId || null,
      createdAt: payment.createdAt || payment.paidAt || new Date().toISOString(),
      updatedAt: payment.updatedAt || payment.paidAt || payment.createdAt || new Date().toISOString(),
    };
  };

  orders = {
    list: async (params?: { 
      startDate?: string; 
      endDate?: string; 
      page?: number; 
      search?: string;
      status?: string;
      paymentStatus?: string;
    }) => {
      const res = await this.request<ApiListResponse<any>>(`/invoices${this.qs(params)}`);
      return {
        ...res,
        data: res.data.map((inv) => this.mapInvoiceToOrder(inv)),
      } as ApiListResponse<ApiOrder>;
    },
    get: async (id: string) => {
      const res = await this.request<ApiDetailResponse<any>>(`/invoices/${id}`);
      return { data: this.mapInvoiceToOrder(res.data) } as ApiDetailResponse<ApiOrder>;
    },
    create: async (data: {
      customerId: string | number;
      orderDate: string;
      items: Array<{ serviceId: string | number; quantity: number; unitPrice?: number }>;
      paymentStatus: 'PAID' | 'UNPAID';
      paymentMethod?: string;
      notes?: string;
    }) => {
      // map payload to createInvoiceSchema (backend expects lines with qty)
      const payload = {
        customerId: typeof data.customerId === 'number' ? data.customerId.toString() : data.customerId,
        // Backend doesn't use orderDate in payload; relies on createdAt
        lines: data.items.map(item => ({
          serviceId: typeof item.serviceId === 'number' ? item.serviceId.toString() : item.serviceId,
          qty: Number(item.quantity),
          unitPrice: item.unitPrice ? Number(item.unitPrice) : undefined,
        })),
        notes: data.notes,
      };

      const res = await this.request<any>('/invoices', {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      // The backend returns { success: true, data: { invoice, date, journalId }, message: '...' }
      const newlyCreatedInvoice = res.data?.invoice || res.data;
      const invoiceId = newlyCreatedInvoice?.id;

      if (!invoiceId) {
          // Fallback if structure is different
           return { data: res.data } as CreateOrderResponse;
      }

      // Fetch full order to return consistent structure mapped to ApiOrder
      const fullOrder = await this.orders.get(invoiceId);
      
      // We don't have full journal entry returned immediately, we'd need to mock or fetch it later if needed in UI
      const journal: JournalEntry | undefined = res.data?.journalId 
         ? ({ id: res.data.journalId, journalNumber: 'JE-PENDING', status: 'BALANCED', lines: [] } as any) 
         : undefined;

      // Note: Payment processing is typically done after invoice creation via /payments, 
      // but if the frontend flow expects it synchronously here, 
      // we mock the success or rely on the UI to make the next call to createPayment.

      return {
        data: {
          order: fullOrder.data,
          journalEntry: journal,
        }
      } as CreateOrderResponse;
    },
    updateOrderItemStatus: async (orderId: string, _itemId: string, _status: string) => {
       // Backend doesn't have an item status endpoint right now. Mocking it or ignoring.
       if (import.meta.env.DEV) {
         console.warn('updateOrderItemStatus is not supported by backend yet');
       }
       return this.request<ApiDetailResponse<ApiOrder>>(`/invoices/${orderId}`, { method: 'GET' });
    },
    updateStatus: async (orderId: string, status: string) => {
        return this.request<ApiDetailResponse<ApiOrder>>(`/invoices/${orderId}/status`, {
            method: 'PATCH',
            body: JSON.stringify({ status }),
        });
    },
    update: async (orderId: string, data: {
      notes?: string;
      paymentStatus?: string;
      items?: Array<{ id?: string; serviceId: string; quantity: number; unitPrice: number }>;
    }) => {
      // Map to backend format
      const payload: any = {};
      if (data.notes !== undefined) payload.notes = data.notes;
      if (data.paymentStatus !== undefined) payload.status = data.paymentStatus === 'PAID' ? 'DONE' : 'PENDING';
      if (data.items !== undefined) {
        payload.lines = data.items.map(item => ({
          ...(item.id ? { id: item.id } : {}),
          serviceId: item.serviceId,
          qty: item.quantity,
          unitPrice: item.unitPrice,
        }));
      }

      const res = await this.request<any>(`/invoices/${orderId}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });

      // Return updated order
      return { data: this.mapInvoiceToOrder(res.data) } as ApiDetailResponse<ApiOrder>;
    },
    getHistory: async (orderId: string): Promise<ApiListResponse<OrderHistoryEntry>> => {
      // Try to fetch from backend first
      try {
        const res = await this.request<ApiListResponse<any>>(`/invoices/${orderId}/history`);
        return {
          ...res,
          data: (res.data || []).map((h: any) => ({
            id: h.id,
            orderId: h.orderId || orderId,
            action: h.action || 'ORDER_CREATED',
            description: h.description || '',
            userId: h.userId || h.user?.id || null,
            userName: h.userName || h.user?.name || h.user?.fullName || null,
            metadata: h.metadata || {},
            createdAt: h.createdAt,
          })),
        };
      } catch {
        // Fallback: return mock data if endpoint doesn't exist yet
        if (import.meta.env.DEV) {
          console.warn('Order history endpoint not available, returning mock data');
        }
        return {
          data: [
            {
              id: '1',
              orderId,
              action: 'ORDER_CREATED',
              description: 'Order was created',
              userId: null,
              userName: 'System',
              createdAt: new Date().toISOString(),
            },
          ],
        };
      }
    },
  };

  // ─── Payments ────────────────────────────────────────
  payments = {
    list: async (params?: { search?: string; paymentMethod?: string; page?: number }) => {
      const hasClientFilters = !!(params?.search || params?.paymentMethod);
      const requestParams = hasClientFilters
        ? { page: 1, limit: 1000 }
        : params;
      const res = await this.request<ApiListResponse<any>>(`/payments${this.qs(requestParams as Record<string, unknown>)}`);
      const mappedPayments = (res.data || []).map(this.mapPayment);
      const searchTerm = params?.search?.trim().toLowerCase();
      const method = params?.paymentMethod
        ? this.normalizePaymentMethod(params.paymentMethod)
        : undefined;

      let filtered = mappedPayments;
      if (searchTerm) {
        filtered = filtered.filter((payment) =>
          payment.paymentNumber.toLowerCase().includes(searchTerm)
          || payment.order.orderNumber.toLowerCase().includes(searchTerm),
        );
      }

      if (method) {
        filtered = filtered.filter((payment) => payment.paymentMethod === method);
      }

      if (res.pagination && !hasClientFilters) {
        return {
          ...res,
          data: filtered,
        } as ApiListResponse<ApiPayment>;
      }

      const pageSize = 50;
      const page = Math.max(1, Number(params?.page || 1));
      const start = (page - 1) * pageSize;

      return {
        data: filtered.slice(start, start + pageSize),
        pagination: {
          page,
          pageSize,
          total: filtered.length,
        },
      } as ApiListResponse<ApiPayment>;
    },
    get: async (id: string | number) => {
      const res = await this.request<ApiDetailResponse<any>>(`/payments/${id}`);
      return {
        ...res,
        data: this.mapPayment(res.data),
      } as ApiDetailResponse<ApiPayment>;
    },
    create: (data: PaymentFormData) => {
      const payload = {
        invoiceId: String(data.orderId),
        amount: data.amount,
        paymentMethod: data.paymentMethod,
      };

      return this.request<CreatePaymentResponse>('/payments', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },
  };

  // ─── Journals ────────────────────────────────────────
  private normalizeJournal = (j: any): JournalEntry => {
    // Calculate totals from lines if not present
    let totalDebit = j.totalDebit;
    let totalCredit = j.totalCredit;
    
    if ((totalDebit === undefined || totalDebit === null) && j.lines) {
      totalDebit = j.lines.reduce((sum: number, line: any) => {
        const debit = parseFloat(line.debit || line.debitAmount || 0);
        return sum + (isNaN(debit) ? 0 : debit);
      }, 0);
    }
    
    if ((totalCredit === undefined || totalCredit === null) && j.lines) {
      totalCredit = j.lines.reduce((sum: number, line: any) => {
        const credit = parseFloat(line.credit || line.creditAmount || 0);
        return sum + (isNaN(credit) ? 0 : credit);
      }, 0);
    }
    
    // Parse totals if they are strings
    totalDebit = typeof totalDebit === 'string' ? parseFloat(totalDebit) : (totalDebit || 0);
    totalCredit = typeof totalCredit === 'string' ? parseFloat(totalCredit) : (totalCredit || 0);
    
    // Map createdByUser to createdBy (Drizzle relation name is createdByUser)
    const creator = j.createdByUser || j.createdBy || j.user || j.creator;
    
    return {
      ...j,
      journalNumber: j.journalNumber || j.entryNumber || j.refNo,
      journalDate: j.journalDate || j.entryDate || j.date,
      totalDebit,
      totalCredit,
      status: j.status || (j.isBalanced ? 'BALANCED' : 'UNBALANCED'),
      createdBy: creator ? {
        id: creator.id,
        name: creator.name || creator.fullName || 'Unknown',
        email: creator.email,
      } : null,
    };
  };

  journals = {
    list: async (params?: { startDate?: string; endDate?: string; type?: string; search?: string; page?: number }) => {
      const res = await this.request<ApiListResponse<any>>(`/journals${this.qs(params)}`);
      return { ...res, data: (res.data || []).map(this.normalizeJournal) } as ApiListResponse<JournalEntry>;
    },
    get: async (id: string) => {
      const res = await this.request<ApiDetailResponse<any>>(`/journals/${id}`);
      return { ...res, data: this.normalizeJournal(res.data) } as ApiDetailResponse<JournalEntry>;
    },
    getByOrder: async (orderId: string) => {
      const res = await this.request<ApiDetailResponse<any>>(`/journals/order/${orderId}`);
      if (Array.isArray(res.data)) {
        return { ...res, data: res.data.map(this.normalizeJournal) } as any;
      }
      return { ...res, data: this.normalizeJournal(res.data) } as ApiDetailResponse<JournalEntry>;
    },
  };

  // ─── Reports ─────────────────────────────────────────
  reports = {
    profitLoss: (startDate: string, endDate: string) =>
      this.request<{ data: ProfitLossReport }>(
        `/reports/profit-and-loss?startDate=${startDate}&endDate=${endDate}`,
      ),
    balanceSheet: (asOfDate: string) =>
      this.request<{ data: BalanceSheetReport }>(
        `/reports/balance-sheet?asOfDate=${asOfDate}`,
      ),
    salesSummary: (startDate?: string, endDate?: string) =>
      this.request<{ data: SalesSummaryData }>(
        `/reports/sales-summary${this.qs({ startDate, endDate })}`,
      ),
    cards: (startDate?: string, endDate?: string) =>
      this.request<{ data: {
        summary: {
          totalRevenue: string;
          totalOrders: number;
          avgOrderValue: string;
        };
        comparison: {
          revenueChange: number;
          ordersChange: number;
          periodDays: number;
        };
        paymentMethods: Array<{
          method: string;
          total: string;
          count: number;
        }>;
        topServices: Array<{
          name: string;
          quantity: number;
          revenue: string;
        }>;
        period: { startDate: string; endDate: string };
      }}>(`/reports/cards${this.qs({ startDate, endDate })}`),
    salesTrend: (startDate?: string, endDate?: string, groupBy?: 'day' | 'week' | 'month') =>
      this.request<{ data: {
        trend: Array<{ period: string; revenue: string; orders: number }>;
        groupBy: string;
        period: { startDate: string; endDate: string };
      }}>(`/reports/cards/sales-trend${this.qs({ startDate, endDate, groupBy })}`),
    agingReceivables: () =>
      this.request<{ data: {
        '0_30': any[];
        '31_60': any[];
        '61_90': any[];
        '90_plus': any[];
      }}>(`/reports/aging-receivables`),
    popularServices: (startDate?: string, endDate?: string) =>
      this.request<{ data: Array<{
        id: string;
        name: string;
        category: string;
        totalVolume: number;
        totalRevenue: number;
      }> }>(`/reports/popular-services${this.qs({ startDate, endDate })}`),
    expenseByCategory: (startDate: string, endDate: string) =>
      this.request<{ data: Array<{
        id: string;
        category: string;
        code: string;
        amount: number;
      }> }>(`/reports/expense-by-category${this.qs({ startDate, endDate })}`),
  };

  // â”€â”€â”€ Dashboard â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  dashboard = {
    cards: (days?: number) =>
      this.request<{ data: {
        totalRevenue: string;
        totalOrders: number;
        avgOrderValue: string;
        totalCustomers: number;
        period: { days: number; startDate: string; endDate: string };
      }}>(`/dashboard/cards${this.qs({ days })}`),
    recentOrders: (limit?: number) =>
      this.request<{ data: Array<{
        id: string;
        invoice_number: string;
        customer_name: string;
        total_amount: string;
        status: string;
        payment_status: string;
        created_at: string;
      }> }>(`/dashboard/recent-orders${this.qs({ limit })}`),
    chartData: (days?: number) =>
      this.request<{ data: {
        dailyRevenue: Array<{ date: string; revenue: string }>;
        period: { days: number; startDate: string; endDate: string };
      }}>(`/dashboard/chart-data${this.qs({ days })}`),
  };

  paymentMethods = {
    list: () => this.request<{ data: any[] }>('/payment-methods'),
    create: (data: any) =>
      this.request('/payment-methods', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    update: (id: number, data: any) =>
      this.request(`/payment-methods/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
  };

  outlets = {
    list: () => this.request<{ data: any[] }>('/outlets'),
    create: (data: any) =>
      this.request('/outlets', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    update: (id: string, data: any) =>
      this.request(`/outlets/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
  };

  accounts = {
    get: (format: 'flat' | 'tree' = 'tree') =>
      this.request<ApiDetailResponse<AccountNode[]>>(`/accounts?format=${format}`),
    setOpeningBalance: (data: { date: string; balances: Array<{ accountCode: string; debit: number; credit: number }> }) =>
      this.request(`/accounts/opening-balance`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  };

  generalLedger = {
    list: (params?: { accountId?: number; periodId?: number; page?: number }) =>
      this.request<ApiListResponse<any>>(`/general-ledger${this.qs(params)}`),
    periods: () =>
      this.request<{ data: any[] }>(`/general-ledger/periods`),
    closePeriod: (id: number) =>
      this.request(`/general-ledger/periods/${id}/close`, { method: 'POST' }),
  };

  // Inventory / Stock
  stockItems = {
    list: (params?: { search?: string; page?: number; limit?: number }) =>
      this.request<ApiListResponse<any>>(`/inventory/stock-items${this.qs(params)}`),
    get: (id: string) =>
      this.request<ApiDetailResponse<any>>(`/inventory/stock-items/${id}`),
    create: (data: { name: string; unit: string; isActive?: boolean }) =>
      this.request<ApiDetailResponse<any>>('/inventory/stock-items', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: { name?: string; unit?: string; isActive?: boolean }) =>
      this.request<ApiDetailResponse<any>>(`/inventory/stock-items/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: (id: string) =>
      this.request<void>(`/inventory/stock-items/${id}`, { method: 'DELETE' }),
  };

  stockMovements = {
    list: (params?: { stockItemId?: string; outletId?: string; type?: string; page?: number }) =>
      this.request<ApiListResponse<any>>(`/inventory/stock-movements${this.qs(params)}`),
    create: (data: { outletId: string; stockItemId: string; type: 'IN' | 'OUT' | 'ADJUST'; qty: number; costPerUnit?: number; notes?: string }) =>
      this.request<ApiDetailResponse<any>>('/inventory/stock-movements', { method: 'POST', body: JSON.stringify(data) }),
  };

  stockLevels = {
    get: (outletId: string, stockItemId: string) =>
      this.request<ApiDetailResponse<any>>(`/inventory/stock-level/${outletId}/${stockItemId}`),
  };

  // User Admin
  users = {
    list: (params?: { search?: string; roleId?: string; isActive?: boolean; page?: number; limit?: number }) =>
      this.request<ApiListResponse<any>>(`/users${this.qs(params)}`),
    get: (id: string) =>
      this.request<ApiDetailResponse<any>>(`/users/${id}`),
    create: (data: { fullName: string; email: string; password: string; roleId?: string; isActive?: boolean }) =>
      this.request<ApiDetailResponse<any>>('/users', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: { fullName?: string; email?: string; roleId?: string; isActive?: boolean }) =>
      this.request<ApiDetailResponse<any>>(`/users/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: (id: string) =>
      this.request<void>(`/users/${id}`, { method: 'DELETE' }),
    resetPassword: (id: string, newPassword: string) =>
      this.request<void>(`/users/${id}/reset-password`, { method: 'POST', body: JSON.stringify({ newPassword }) }),
  };

  roles = {
    list: (params?: { page?: number; limit?: number }) =>
      this.request<ApiListResponse<any>>(`/users/roles${this.qs(params)}`),
    get: (id: string) =>
      this.request<ApiDetailResponse<any>>(`/users/roles/${id}`),
    create: (data: { name: string; description?: string; permissions: Record<string, string[]> }) =>
      this.request<ApiDetailResponse<any>>('/users/roles', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: { name?: string; description?: string; permissions?: Record<string, string[]> }) =>
      this.request<ApiDetailResponse<any>>(`/users/roles/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: (id: string) =>
      this.request<void>(`/users/roles/${id}`, { method: 'DELETE' }),
  };

  expenses = {
    list: (params?: { startDate?: string; endDate?: string; category?: string; page?: number; limit?: number }) =>
      this.request<ApiListResponse<ApiExpense>>(`/expenses${this.qs(params)}`),
    get: (id: string) =>
      this.request<ApiDetailResponse<ApiExpense>>(`/expenses/${id}`),
    create: (data: ExpenseFormData) =>
      this.request<ApiDetailResponse<ApiExpense>>('/expenses', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    getAccounts: (category?: string) =>
      this.request<ApiDetailResponse<Array<{ id: number; name: string; code: string }>>>(`/expenses/accounts${this.qs({ category })}`),
  };

  // ─── Order Steps (Production Tracking) ────────────────
  orderSteps = {
    list: (invoiceId: string) =>
      this.request<{ success: boolean; data: OrderStep[] }>(`/invoices/${invoiceId}/steps`),
    start: (invoiceId: string, step: string) =>
      this.request<{ success: boolean; data: OrderStep }>(`/invoices/${invoiceId}/steps/start`, {
        method: 'POST',
        body: JSON.stringify({ step }),
      }),
    complete: (invoiceId: string, step: string) =>
      this.request<{ success: boolean; data: OrderStep }>(`/invoices/${invoiceId}/steps/complete`, {
        method: 'POST',
        body: JSON.stringify({ step }),
      }),
  };

  // ─── Alerts ──────────────────────────────────────────
  alerts = {
    list: (params?: { isRead?: boolean; limit?: number }) =>
      this.request<{ data: Alert[]; unreadCount: number }>(`/alerts${this.qs(params)}`),
    markAsRead: (id: string) =>
      this.request<{ data: Alert }>(`/alerts/${id}/read`, { method: 'PATCH' }),
    markAllAsRead: () =>
      this.request<{ data: { count: number } }>(`/alerts/read-all`, { method: 'POST' }),
  };
}

export interface OrderStep {
  id: string;
  tenantId: string;
  invoiceId: string;
  step: string;
  startedAt: string | null;
  completedAt: string | null;
  assignedTo: string | null;
  createdAt: string;
  assignedUser?: { id: string; fullName: string; email: string } | null;
}

export interface Alert {
  id: string;
  tenantId: string;
  type: string;
  title: string;
  message: string;
  referenceId: string | null;
  isRead: boolean;
  createdAt: string;
}

export const api = new APIClient();
